<template>
  <div
    ref="container"
    v-click-outside="handleClickOutside"
    class="quick-add"
  >
    <teleport
      :disabled="!teleport"
      :to="typeof teleport === 'string' ? teleport : undefined"
    >
      <transition
        v-if="options.length"
        name="fade"
      >
        <div
          v-if="showOptions"
          ref="optionsOverlay"
          class="quick-add__options-wrapper"
          :class="{
            [$class('moved')]: teleport,
            ['quick-add']: teleport,
          }"
        >
          <!-- Quick add overlay -->
          <button
            type="button"
            :aria-label="$string('product_card.close_quick_add')"
            class="quick-add__close button--reset"
            @click="handleClose"
          />

          <!-- Quick add selects -->
          <div class="quick-add__options form-group">
            <select
              v-for="option of options"
              ref="select"
              :key="option.name"
              v-model="selectedOptions[option.index]"
              class="text-center"
              @keydown.enter.prevent
            >
              <option
                disabled
                :value="undefined"
                v-text="
                  $string('product_card.select_option', {
                    replace: {
                      option: option.name,
                    },
                  })
                "
              />
              <option
                v-for="value of option.values"
                :key="value"
                :value="{
                  name: option.name,
                  value,
                }"
                :disabled="isOptionOutOfStock(option, value)"
                v-text="getOptionLabel(option, value)"
              />
            </select>

            <!-- ATC in modal when full screen -->
            <btn
              v-if="isFullScreen"
              :label="btnLabel"
              class="quick-add__add"
              :disabled="isFullScreenBtnDisabled"
              :show-state-icon="state === 'adding'"
              type="button"
              @click="handleAddClick"
            >
              <template #stateIcon>
                <icon-loading />
              </template>
            </btn>

            <!-- Mobile quick add error -->
            <transition name="fade">
              <span
                v-if="error && isFullScreen"
                class="text-body-xs-regular quick-add__error"
                v-text="error"
              />
            </transition>
          </div>
        </div>
      </transition>
    </teleport>

    <!-- Quick add trigger and desktop atc -->
    <btn
      ref="trigger"
      :modifiers="buttonModifiers"
      :label="isFullScreen ? fullScreenBtnLabel : btnLabel"
      class="quick-add__add"
      :disabled="isTriggerDisabled"
      :show-state-icon="state === 'adding'"
      type="button"
      @click="handleAddClick"
    >
      <template #stateIcon>
        <icon-loading />
      </template>
    </btn>

    <!-- Desktop error -->
    <transition name="fade">
      <span
        v-if="error && !isFullScreen"
        class="text-body-xs-regular quick-add__error"
        v-text="error"
      />
    </transition>
  </div>
</template>

<script>
/**
 * Vue: Quick add (quick-add)
 * -----------------------------------------------------------------------------
 * Quick add variant to cart.
 *
 * @param {Boolean | String} teleport - Teleport quick add options to this
 * selector
 * @param {Boolean} [useFullScreen] - Use full screen functionality. By default,
 * mobile uses full screen functionality. This prop will force full screen on
 * both mobile and desktop
 */

import { mapActions, mapGetters, mapState } from 'vuex'
import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock'

import IconLoading from '~icons/general/loading.svg'

import Btn from '~global/btn/btn'

import { addToCartEvent } from '~/helpers/datalayer-actions'
import { getLineItemProperties, getSortedOptionValues } from '~/helpers/product'
import { emitAddedLineItem, emitUpdateVariant } from '~/helpers/vue'
import { openCart } from '~/helpers/cart'

export default {
  name: 'QuickAdd',

  components: {
    IconLoading,
    Btn,
  },

  props: {
    buttonModifiers: {
      type: [String, Array],
      default: 'primary block center',
    },
    lineProps: {
      type: Object,
      default: () => ({}),
    },
    product: {
      type: Object,
      default: null,
    },
    teleport: {
      type: [Boolean, String],
      default: false,
    },
    useFullScreen: {
      type: Boolean,
      default: false,
    },
    variantId: {
      type: Number,
      default: null,
    },
  },

  emits: {
    'update-variant': emitUpdateVariant,
    'add-cart': emitAddedLineItem,
    'before-add-cart': () => true,
    'add-cart-error': () => true,
  },

  data() {
    return {
      error: '',
      showOptions: false,
      selectedOptions: [],
      state: 'ready',
      timeout: null,
    }
  },

  computed: {
    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getFirstElement: 'accessibility/getFirstElement',
      getActiveOverlay: 'overlays/getActiveOverlay',
      getComponentExists: 'components/getExists',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart.response,
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute button label
     * @returns {string}
     */
    btnLabel() {
      /**
       * Product is out of stock
       */
      if (!this.product.available) {
        return this.$string('product_card.out_of_stock')
      }

      if (this.isVariantInCart) {
        return this.$string('product_card.added_to_bag')
      }

      if (this.product?.tags.includes('preorder')) {
        return this.$string('product_card.preorder')
      }

      /**
       * Prompt user to select option
       */
      const namesLen = this.missingOptionNames.length
      if (this.showOptions && namesLen) {
        if (namesLen === 1) {
          return this.$string('product_card.select_option', {
            replace: {
              option: this.missingOptionNames[0],
            },
          })
        }
        return this.$string('product_card.select_options')
      }

      /**
       * Add to cart states
       */
      switch (this.state) {
        case 'added':
          return this.$string('product_card.added_to_bag')
        default:
          return this.$string('product_card.add_to_bag')
      }
    },

    /**
     * Compute if using full screen behavior.
     * Full screen behavior is used on mobile or when prop specifically uses it.
     * @returns {boolean}
     */
    isFullScreen() {
      return this.isMobile || this.useFullScreen
    },

    /**
     * Compute if variant is in cart
     * @returns {boolean}
     */
    isVariantInCart() {
      if (!this.variant) {
        return false
      }
      return this.cart.items.some((item) => item.variant_id === this.variant.id)
    },

    /**
     * Compute simple mobile button label
     * @returns {string}
     */
    fullScreenBtnLabel() {
      if (!this.product.available) {
        return this.$string('product_card.out_of_stock')
      }

      if (this.isVariantInCart) {
        return this.$string('product_card.added_to_bag')
      }

      if (this.product?.tags.includes('preorder')) {
        return this.$string('product_card.preorder')
      }

      return this.$string('product_card.add_to_bag')
    },

    /**
     * Compute if quick add trigger button is disabled.
     * @returns {boolean}
     */
    isTriggerDisabled() {
      if (!this.product.available) {
        return true
      }

      if (this.isFullScreen) {
        return false
      }

      return this.showOptions && this.missingOptionNames.length > 0
    },

    /**
     * Compute if full screen button is disabled
     * @returns {boolean}
     */
    isFullScreenBtnDisabled() {
      return this.missingOptionNames.length > 0 || !this.product.available
    },

    /**
     * Compute option names that still need to be selected
     * @returns {[]}
     */
    missingOptionNames() {
      const names = []
      this.options.forEach((option) => {
        if (!this.selectedOptions[option.index]) {
          names.push(option.name)
        }
      })
      return names
    },

    /**
     * Compute product options where option contains more than one value
     * @returns {Array}
     */
    options() {
      const options = []

      this.product.options.forEach((option) => {
        if (option.values.length < 2) {
          return
        }

        options.push({
          ...option,
          values: getSortedOptionValues(option.values),
        })
      })

      return options
    },

    /**
     * Compute selected variant based on the selected options
     * @returns {Object|null}
     */
    variant() {
      return this.product.variants.find((variant) => {
        return this.areSelectedOptionsEqual(variant.selectedOptions, this.selectedOptions)
      })
    },
  },

  watch: {
    /**
     * Reset error state
     */
    error() {
      if (!this.error) {
        return
      }
      clearTimeout(this.timeout)
      this.timeout = setTimeout(() => {
        this.error = ''
      }, this.$timing('message'))
    },

    /**
     * Reset cart add state
     */
    state() {
      if (this.state === 'added') {
        clearTimeout(this.timeout)
        this.timeout = setTimeout(() => {
          this.state = 'ready'
        }, this.$timing('message'))
      }
    },

    /**
     * Emit variant when updated
     */
    variant() {
      this.$emit('update-variant', this.variant)
    },

    /**
     * Reset focus trap when mobile btn goes from disabled to enabled.
     * Disabled buttons are excluded from focus traps.
     */
    async isFullScreenBtnDisabled(disabled) {
      if (disabled || !this.isFullScreen || !this.showOptions || !this.$refs.optionsOverlay) {
        return
      }

      await this.clearFocusTrap()
      this.setFocusTrap({
        container: this.$refs.optionsOverlay,
      })
    },

    /**
     * Refocus to first visible select if the atc button is disabled
     * @param disabled
     */
    async isTriggerDisabled(disabled) {
      await this.$nextTick()
      if (this.isFullScreen || !disabled || !this.showOptions || !this.$refs.select?.length) {
        return
      }
      cnvs.Focus(this.$refs.select[0])
    },

    /**
     * Toggle body scroll lock and trap focus when options are visible.
     * Only applies when using full screen
     */
    async showOptions(visible) {
      await this.$nextTick()

      if (!this.isFullScreen) {
        return
      }

      /**
       * Clean up locks and traps
       */
      if (!visible) {
        this.handleClose()
        return
      }

      /**
       * Turn on locks and traps options are showing and when using full screen
       */
      if (!this.$refs.optionsOverlay) {
        return
      }

      disableBodyScroll(this.$refs.optionsOverlay)

      await this.setFocusTrap({
        container: this.$refs.optionsOverlay,
      })

      if (this.getFirstElement) {
        cnvs.Focus(this.getFirstElement)
      }
    },
  },

  created() {
    this.initSelectedOptions()
  },

  mounted() {
    this.setEventListeners()
  },

  methods: {
    /**
     * Map Vuex actions.
     */
    ...mapActions({
      clearFocusTrap: 'accessibility/clearFocusTrap',
      setFocusTrap: 'accessibility/setFocusTrap',
      addItem: 'cart/addItem',
      openOverlay: 'overlays/open',
    }),

    /**
     * Show quick add options if hidden or add to cart if visible
     */
    async handleAddClick() {
      if (!this.variant || (!this.showOptions && this.options.length > 0)) {
        this.showOptions = true
        return
      }

      this.state = 'adding'
      this.$emit('before-add-cart')

      try {
        const properties = getLineItemProperties(this.variant, this.product) || {}
        const items = [
          {
            id: this.variant.id,
            properties: Object.assign(properties, this.lineProps),
            quantity: 1,
          },
        ]

        const response = await this.addItem(items)
        this.state = 'added'
        this.$emit('add-cart', {
          lineItem: response.items[0],
          product: this.product,
          variant: this.variant,
        })
        this.handleClose()

        addToCartEvent(this.variant, this.product)

        await this.$nextTick()

        openCart(this.getComponentExists, this.openOverlay)
      } catch (error) {
        this.state = 'ready'
        this.$emit('add-cart-error', error?.description)

        if (error?.description) {
          const transformedTitle = `${this.product.originalTitle} - ${this.variant.title}`
          this.error = error.description.replace(transformedTitle, this.product.title)
        }

        cnvs.ReportError('[Quick Add - Add to cart]', error)
      }
    },

    /**
     * Close quick add when clicking outside of quick add area on desktop
     * @param event - Click event
     */
    handleClickOutside(event) {
      const { container } = this.$refs
      if (!container || container.contains(event.target) || this.isFullScreen) {
        return
      }
      this.handleClose()
    },

    /**
     * Close quick add overlay and, if mobile,
     * clear scroll lock and focus traps.
     */
    async handleClose() {
      this.showOptions = false

      if (!this.isFullScreen) {
        return
      }

      /**
       * Clean up focus traps and body locks
       */
      clearAllBodyScrollLocks()
      await this.clearFocusTrap()

      if (this.getActiveOverlay) {
        cnvs.EventBus.emit('overlay:update', {
          namespace: this.getActiveOverlay.namespace,
          currentElement: this.$refs?.trigger?.$el,
        })
      }

      if (this.$refs?.trigger) {
        cnvs.Focus(this.$refs.trigger.$el)
      }
    },

    /**
     * Generate option label text
     * @param option
     * @param value
     * @returns {string}
     */
    getOptionLabel(option, value) {
      const oos = this.isOptionOutOfStock(option, value)
      return oos ? `${value} ${this.$string('product_card.out_of_stock_dropdown')}`.trim() : value
    },

    /**
     * Check if both options are equal
     * @param {{name: string, value: string}} opt1
     * @param {{name: string, value: string}} opt2
     * @returns {boolean}
     */
    areOptionsEqual(opt1, opt2) {
      return opt1?.name === opt2?.name && opt1?.value === opt2?.value
    },

    /**
     * Check if both selected options arrays are equal
     * @param {{name: string, value: string}[]} options1 - Selected options 1
     * @param {{name: string, value: string}[]} options2 - Selected options 2
     * to compare with 1
     * @returns {boolean}
     */
    areSelectedOptionsEqual(options1, options2) {
      const opts1 = options1 || []
      const opts2 = options2 || []

      return opts1.every((opt, index) => {
        return this.areOptionsEqual(opt, opts2[index])
      })
    },

    /**
     * Check if option is out of stock relative to other selected options
     * @param {Object} option - Product option
     * @param {String} value - Option value
     * @returns {Boolean}
     */
    isOptionOutOfStock(option, value) {
      const simulated = this.selectedOptions.slice()
      simulated[option.index] = { name: option.name, value }

      const potentialVariant = this.product.variants.find((variant) => {
        return (
          variant.available &&
          simulated.every((selectedOpt, index) => {
            if (!selectedOpt) {
              return true
            }
            return this.areOptionsEqual(variant.selectedOptions[index], selectedOpt)
          })
        )
      })

      return !potentialVariant
    },

    /**
     * Initialize selected options
     */
    initSelectedOptions() {
      /**
       * Use variant prop if possible
       */
      if (this.variantId) {
        const variant = this.product.variants.find((pVariant) => {
          return pVariant.id === this.variantId
        })

        if (variant?.available) {
          this.selectedOptions = variant.selectedOptions.slice()
          return
        }
      }

      this.selectedOptions.length = this.product.options.length

      /**
       * Set single options
       */
      this.product.options.forEach((option) => {
        if (option.values.length > 1) {
          return
        }
        this.selectedOptions[option.index] = {
          name: option.name,
          value: option.values[0],
        }
      })
    },

    /**
     * Set event listeners for escape and tab key presses
     * - Sleep after tab press because new active element doesn't register
     * immediately
     * - Close when leaving quick add area (non-full screen)
     */
    setEventListeners() {
      cnvs.EventBus.listen('cnvs:esc', this.handleClose)
      cnvs.EventBus.listen('cnvs:tab', async () => {
        if (this.isFullScreen || !this.$refs.container) {
          return
        }

        await cnvs.Sleep(1)

        if (this.$refs.container.contains(document.activeElement)) {
          return
        }

        this.handleClose()
      })
    },
  },
}
</script>

<style lang="scss">
@import './quick-add';
</style>
